// src/logic/helpers/audio.ts
//
// Tiny SFX service mirroring alpha-mini-app' AudioService semantics (they use
// Howler; this is the dependency-free Web Audio equivalent):
//   - playSound(sound, delayMs, randomizePitch): fire-and-forget playback,
//     optional start delay and 0.95-1.1x pitch randomization for repeats
//   - preloadSounds([...]): fetch + decode ahead of first use
//   - master volume normalized to 0.5 (alpha-mini-app: normalizeVolume = v/100*.5)
//   - hard-muted while the tab is hidden (alpha-mini-app: Howler.mute(hidden))
//   - managed playbacks can fade out on cancel so a cut never clicks
//   - resumeAudio()/subscribeToAudioState(): let a user gesture unlock the
//     context and let previews show when it is still waiting for one
//
// Resource footprint: one shared AudioContext, at most two fetch/decode jobs,
// shared pending requests, and up to 32MiB of retained decoded buffers.
// Playback nodes release their own references when they finish.

import { createAudioBufferCache } from "./audioBufferCache.ts";

const SFX_VOLUME = 0.5;
const DECODED_AUDIO_CACHE_BYTES = 32 * 1024 * 1024;
const AUDIO_LOAD_TIMEOUT_MS = 15_000;

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let soundEffectsEnabled = true;
let unlockListenersInstalled = false;

function installAudioUnlockListeners(): void {
    if (unlockListenersInstalled || typeof document === "undefined") return;
    unlockListenersInstalled = true;
    const unlock = (): void => {
        if (soundEffectsEnabled) resumeAudio();
    };
    document.addEventListener("pointerdown", unlock, { capture: true });
    document.addEventListener("keydown", unlock, { capture: true });
}

const bufferCache = createAudioBufferCache<AudioBuffer>({
    load: async (url) => {
        const context = ensureContext();
        if (!context) return null;
        const abort = new AbortController();
        const timeout = globalThis.setTimeout(
            () => abort.abort(),
            AUDIO_LOAD_TIMEOUT_MS,
        );
        try {
            const response = await fetch(url, { signal: abort.signal });
            if (!response.ok) return null;
            return await context.decodeAudioData(await response.arrayBuffer());
        } catch {
            return null;
        } finally {
            globalThis.clearTimeout(timeout);
        }
    },
    sizeOf: (buffer) => buffer.length * buffer.numberOfChannels * 4,
    maxBytes: DECODED_AUDIO_CACHE_BYTES,
    concurrency: 2,
});

function getRandom(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function applyMasterVolume(): void {
    if (!masterGain) return;
    masterGain.gain.value =
        soundEffectsEnabled && !document.hidden ? SFX_VOLUME : 0;
}

export function setSoundEffectsEnabled(enabled: boolean): void {
    // The existing settings bridge runs before the first scene interaction.
    // Register gestures now without creating or decoding any audio yet.
    installAudioUnlockListeners();
    const wasEnabled = soundEffectsEnabled;
    soundEffectsEnabled = enabled;
    applyMasterVolume();
    if (enabled && !wasEnabled) resumeAudio();
}

/** A suspended context may never fire `ended`; settle a fade by clock too. */
const FADE_FINISH_GRACE_MS = 50;

export type AudioState = AudioContextState | "unavailable";

// ___lazily create the context; must first run inside a user gesture
// (pointer/click handler) or mobile WebViews keep it suspended
function ensureContext(): AudioContext | null {
    installAudioUnlockListeners();
    if (typeof AudioContext === "undefined") return null;
    if (!ctx) {
        ctx = new AudioContext();
        masterGain = ctx.createGain();
        applyMasterVolume();
        masterGain.connect(ctx.destination);

        document.addEventListener("visibilitychange", () => {
            applyMasterVolume();
            if (!document.hidden) resumeAudio();
        });
    }
    // ___resume is a no-op when already running; needed after the WebView
    // auto-suspends (backgrounding, audio interruptions)
    if (ctx.state !== "running" && ctx.state !== "closed")
        void ctx.resume().catch(() => undefined);
    return ctx;
}

/**
 * Create or resume the AudioContext. Call from a user-gesture handler: a
 * context created before the page's first gesture stays suspended until one
 * happens, and nothing scheduled in the meantime is heard.
 */
export function resumeAudio(): void {
    ensureContext();
}

/**
 * Report the AudioContext state now and whenever it changes, creating the
 * context if needed. Lets a preview show a "tap to enable sound" affordance
 * only while the browser is still waiting for a gesture.
 */
export function subscribeToAudioState(
    listener: (state: AudioState) => void,
): () => void {
    const context = ensureContext();
    if (!context) {
        listener("unavailable");
        return () => undefined;
    }

    const notify = (): void => listener(context.state);
    context.addEventListener("statechange", notify);
    notify();
    return () => context.removeEventListener("statechange", notify);
}

function loadBuffer(url: string, urgent = true): Promise<AudioBuffer | null> {
    return bufferCache.get(url, urgent);
}

/** Read-only availability; a caller can follow its own presentation clock. */
export function isSoundPlaybackAvailable(): boolean {
    return soundEffectsEnabled && !document.hidden && ctx?.state === "running";
}

export interface SoundPlaybackResources {
    context: AudioContext;
    destination: AudioNode;
    buffer: AudioBuffer;
}

/** Prepared sequences share the same decode cache, settings and output graph. */
export async function prepareSoundPlayback(
    url: string,
): Promise<SoundPlaybackResources | null> {
    if (!soundEffectsEnabled) return null;
    const context = ensureContext();
    if (!context) return null;
    const buffer = await loadBuffer(url);
    if (!buffer || !masterGain || !isSoundPlaybackAvailable()) return null;
    return { context, destination: masterGain, buffer };
}

/**
 * Fetch and decode sounds ahead of time (alpha-mini-app: preloadSounds).
 * Call from a user-gesture handler so the AudioContext can start.
 */
export function preloadSounds(urls: readonly string[]): void {
    if (!soundEffectsEnabled) return;

    ensureContext();
    for (const url of urls) void loadBuffer(url, false);
}

export type SoundPlaybackResult = "ended" | "cancelled" | "unavailable";

export interface ManagedSoundPlayback {
    cancel: (fadeOutMs?: number) => void;
    finished: Promise<SoundPlaybackResult>;
}

type SoundPitchVariation =
    boolean | readonly [minRate: number, maxRate: number];

export interface ManagedSoundPlaybackOptions {
    delayMs?: number;
    /** Resolved after decoding, immediately before starting; null drops a stale cue. */
    getOffsetSeconds?: () => number | null;
    /** Reports an actual source start, never a queued or blocked attempt. */
    onStarted?: (offsetSeconds: number) => void;
    /** Ramps the sound to silence over this long when it is cancelled. */
    fadeOutMs?: number;
    /** True uses 0.95-1.1x; a range sets custom playback-rate bounds. */
    randomizePitch?: SoundPitchVariation;
    volume?: number;
}

/**
 * Play a sound effect (alpha-mini-app: playSound).
 *
 * @param url            asset url (Vite-imported)
 * @param delayMs        delay before playback starts
 * @param randomizePitch true varies playback rate 0.95-1.1x; a [min, max]
 *                       range customizes the variation for this sound
 * @param volume         per-sound level 0-1, relative to the master volume
 *                       (e.g. to keep a looping bed under the stingers)
 * @returns              a cancellation function; callers may ignore it for
 *                       ordinary fire-and-forget playback
 */
export function playSound(
    url: string,
    delayMs = 0,
    randomizePitch: SoundPitchVariation = false,
    volume = 1,
): () => void {
    return playManagedSound(url, {
        delayMs,
        randomizePitch,
        volume,
    }).cancel;
}

/**
 * Play a sound effect and expose its lifecycle to coordinated schedulers.
 */
export function playManagedSound(
    url: string,
    {
        delayMs = 0,
        fadeOutMs = 0,
        randomizePitch = false,
        volume = 1,
        getOffsetSeconds,
        onStarted,
    }: ManagedSoundPlaybackOptions = {},
): ManagedSoundPlayback {
    return createSoundPlayback(url, {
        delayMs,
        fadeOutMs,
        randomizePitch,
        volume,
        getOffsetSeconds,
        onStarted,
    });
}

interface SoundPlaybackOptions {
    getOffsetSeconds?: () => number | null;
    onStarted?: (offsetSeconds: number) => void;
    delayMs: number;
    fadeOutMs: number;
    randomizePitch: SoundPitchVariation;
    volume: number;
}

function createSoundPlayback(
    url: string,
    {
        delayMs,
        fadeOutMs,
        randomizePitch,
        volume,
        getOffsetSeconds,
        onStarted,
    }: SoundPlaybackOptions,
): ManagedSoundPlayback {
    let cancelled = false;
    let finishedResult: SoundPlaybackResult | null = null;
    let source: AudioBufferSourceNode | null = null;
    let perPlayGain: GainNode | null = null;
    let resolveFinished: (result: SoundPlaybackResult) => void = () =>
        undefined;
    const finished = new Promise<SoundPlaybackResult>((resolve) => {
        resolveFinished = resolve;
    });

    const releaseNodes = () => {
        source?.disconnect();
        perPlayGain?.disconnect();
    };

    const finish = (result: SoundPlaybackResult) => {
        if (finishedResult !== null) return;

        finishedResult = result;
        releaseNodes();
        resolveFinished(result);
    };

    // ___a fade hands the ending to `onended`, which reports it cancelled
    const fadeOutAndStop = (durationMs: number): boolean => {
        if (!source || !perPlayGain || !ctx || durationMs <= 0) return false;

        const now = ctx.currentTime;
        const stopAt = now + durationMs / 1000;
        try {
            perPlayGain.gain.cancelScheduledValues(now);
            perPlayGain.gain.setValueAtTime(perPlayGain.gain.value, now);
            perPlayGain.gain.linearRampToValueAtTime(0, stopAt);
            source.stop(stopAt);
        } catch {
            return false;
        }
        window.setTimeout(
            () => finish("cancelled"),
            durationMs + FADE_FINISH_GRACE_MS,
        );
        return true;
    };

    const cancel = (fadeOverrideMs = fadeOutMs) => {
        if (cancelled) return;

        cancelled = true;
        if (source && finishedResult === null) {
            if (fadeOutAndStop(fadeOverrideMs)) return;
            try {
                source.stop();
            } catch {
                // The source may already have ended or not have reached its start.
            }
        }
        finish("cancelled");
    };

    if (!soundEffectsEnabled) {
        finish("unavailable");
        return { cancel, finished };
    }

    const context = ensureContext();
    if (!context || !masterGain) {
        finish("unavailable");
        return { cancel, finished };
    }

    void loadBuffer(url).then((buffer) => {
        if (
            cancelled ||
            !soundEffectsEnabled ||
            !buffer ||
            !ctx ||
            !masterGain
        ) {
            if (!cancelled) finish("unavailable");
            return;
        }

        // Clock-synchronized callers must never queue old sounds in a suspended context.
        const offset = getOffsetSeconds === undefined ? 0 : getOffsetSeconds();
        if (
            offset === null ||
            !Number.isFinite(offset) ||
            offset < 0 ||
            offset >= buffer.duration ||
            (getOffsetSeconds !== undefined && !isSoundPlaybackAvailable())
        ) {
            finish("unavailable");
            return;
        }
        source = ctx.createBufferSource();
        source.buffer = buffer;
        if (randomizePitch) {
            const [minRate, maxRate] =
                randomizePitch === true ? [0.95, 1.1] : randomizePitch;
            source.playbackRate.value = getRandom(minRate, maxRate);
        }

        // ___per-play gain node only when attenuating or fading; disconnected
        // on end so the throwaway graph is released with the source node
        let destination: AudioNode = masterGain;
        if (volume !== 1 || fadeOutMs > 0) {
            perPlayGain = ctx.createGain();
            perPlayGain.gain.value = volume;
            perPlayGain.connect(masterGain);
            destination = perPlayGain;
        }

        source.connect(destination);
        source.onended = () => {
            finish(cancelled ? "cancelled" : "ended");
        };
        try {
            source.start(
                delayMs > 0 ? ctx.currentTime + delayMs / 1000 : 0,
                offset,
            );
            onStarted?.(offset);
        } catch {
            finish("unavailable");
        }
    });

    return { cancel, finished };
}

interface TimedFadeSoundOptions {
    durationMs: number;
    fadeOutMs: number;
    volume?: number;
}

/**
 * Play one sound for a bounded duration and linearly fade its final section.
 *
 * The returned function cancels playback even when decoding is still pending,
 * which lets callers replace an earlier music cue without overlapping tracks.
 */
export function playTimedFadeSound(
    url: string,
    { durationMs, fadeOutMs, volume = 1 }: TimedFadeSoundOptions,
): () => void {
    if (!soundEffectsEnabled) return () => undefined;

    const context = ensureContext();
    if (!context || !masterGain) return () => undefined;

    let cancelled = false;
    let ended = false;
    let source: AudioBufferSourceNode | null = null;
    let perPlayGain: GainNode | null = null;

    const stop = () => {
        cancelled = true;
        if (!source || ended) return;
        ended = true;
        try {
            source.stop();
        } catch {
            // The source may already have reached its scheduled stop.
        }
        source.disconnect();
        perPlayGain?.disconnect();
    };

    void loadBuffer(url).then((buffer) => {
        if (
            cancelled ||
            !soundEffectsEnabled ||
            !buffer ||
            !ctx ||
            !masterGain
        ) {
            return;
        }

        const totalSeconds = Math.min(
            Math.max(0, durationMs) / 1000,
            buffer.duration,
        );
        if (totalSeconds === 0) return;

        const fadeSeconds = Math.min(
            totalSeconds,
            Math.max(0, fadeOutMs) / 1000,
        );
        const startAt = ctx.currentTime;
        const stopAt = startAt + totalSeconds;
        const fadeAt = stopAt - fadeSeconds;

        source = ctx.createBufferSource();
        source.buffer = buffer;
        perPlayGain = ctx.createGain();
        perPlayGain.gain.setValueAtTime(volume, startAt);
        if (fadeSeconds > 0) {
            perPlayGain.gain.setValueAtTime(volume, fadeAt);
            perPlayGain.gain.linearRampToValueAtTime(0, stopAt);
        }
        perPlayGain.connect(masterGain);
        source.connect(perPlayGain);
        source.onended = () => {
            if (ended) return;
            ended = true;
            source?.disconnect();
            perPlayGain?.disconnect();
        };
        source.start(startAt);
        source.stop(stopAt);
    });

    return stop;
}
